/**
 * Superadmin → Platform insights → computed KPI cards (tracking plan §A1).
 * Reads `GET /admin/platform-insights/kpis` (daily snapshots computed from
 * Tyashin's own data) through TanStack Query + DataState (CLAUDE.md §12).
 * A KPI the backend cannot compute shows WHY, never a placeholder number.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, Info, Minus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { DataState } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { analyticsEmbedHelpers } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { qk } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import {
  FEATURE_LABEL,
  KPI_META,
  formatKpiValue,
  kpiDelta,
  sparklinePath,
  type KpiKey,
  type KpiSummary,
  type PlatformKpis,
} from '@/lib/platformKpis';
import type { DateRange } from '@/lib/yom/range';

const SECTIONS: { title: string; description: string; keys: KpiKey[] }[] = [
  {
    title: 'North star and activation',
    description: 'Are sites live and producing customers, and how fast do new teams get there.',
    keys: ['wvs', 'activation', 'aha', 'ttvPublish', 'ttvLead'],
  },
  {
    title: 'Retention and engagement',
    description: 'Do builders come back, and which features live sites actually use.',
    keys: [
      'retentionD1',
      'retentionD7',
      'retentionD30',
      'builderWau',
      'builderMau',
      'stickiness',
      'adoption',
    ],
  },
  {
    title: 'Revenue and cost',
    description: 'Conversion to paid and what AI costs per active project.',
    keys: ['signupToPaid', 'aiCostPerActiveProject'],
  },
  {
    title: 'Reliability',
    description: 'Deploys and storefront speed as customers experience them.',
    keys: ['deploySuccess', 'cwvPass'],
  },
];

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** One line under the value saying what the number is made of. */
function detailLine(k: KpiSummary): string | null {
  const d = k.detail ?? {};
  if (k.kind === 'ratio') {
    const n = num(d.num);
    const den = num(d.den);
    if (n == null || den == null) return null;
    const noun = k.key === 'deploySuccess' ? 'deploys' : 'teams in the cohort';
    return `${n} of ${den} ${noun}`;
  }
  if (k.kind === 'median') {
    const n = num(d.samples);
    return n ? `${n} team${n === 1 ? '' : 's'} reached it` : null;
  }
  switch (k.key) {
    case 'wvs':
      return num(d.liveProjects) != null ? `of ${d.liveProjects} live sites` : null;
    case 'cwvPass':
      return `${d.passing ?? 0} of ${d.measured ?? 0} measured sites (${
        d.source === 'yom' ? 'YOM' : 'Google CrUX'
      }), ${d.liveProjects ?? 0} live`;
    case 'aiCostPerActiveProject':
      return `${formatKpiValue('usd', num(d.totalUsd))} over ${d.activeProjects ?? 0} active projects`;
    case 'adoption':
      return `${d.usingAnyFeature ?? 0} of ${d.liveProjects ?? 0} live sites`;
    default:
      return null;
  }
}

function Sparkline({ series }: { series: KpiSummary['series'] }) {
  const path = sparklinePath(series, 120, 32);
  if (!path) return <div className="h-8" aria-hidden />;
  return (
    <svg
      viewBox="0 0 120 32"
      preserveAspectRatio="none"
      className="text-primary h-8 w-full"
      role="img"
      aria-label="Daily trend"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function DeltaChip({ k }: { k: KpiSummary }) {
  const d = kpiDelta(k);
  if (!d) return <span className="text-muted-foreground text-xs">no earlier data</span>;
  const Icon =
    d.direction === 'up' ? ArrowUpRight : d.direction === 'down' ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
        d.tone === 'good' &&
          'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
        d.tone === 'bad' && 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300',
        d.tone === 'neutral' && 'bg-muted text-muted-foreground'
      )}
      title={`Previous period: ${formatKpiValue(k.unit, k.previous)}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {d.text}
    </span>
  );
}

function AdoptionBars({ k }: { k: KpiSummary }) {
  const d = k.detail ?? {};
  const live = num(d.liveProjects) ?? 0;
  const features = (d.features ?? {}) as Record<
    string,
    { enabled: number | null; used: number | null }
  >;
  if (!live) return null;
  return (
    <ul className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
      {Object.entries(features).map(([key, f]) => {
        const main = f.used ?? f.enabled;
        const pct = main == null ? 0 : Math.round((main / live) * 100);
        return (
          <li key={key} className="text-xs">
            <div className="flex justify-between gap-2">
              <span>{FEATURE_LABEL[key] ?? key}</span>
              <span className="text-muted-foreground tabular-nums">
                {f.enabled != null ? `${f.enabled} on` : ''}
                {f.enabled != null && f.used != null ? ' · ' : ''}
                {f.used != null ? `${f.used} used` : ''}
              </span>
            </div>
            <div className="bg-muted mt-1 h-1.5 overflow-hidden rounded-full">
              <div className="bg-primary h-full rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function KpiCard({ k }: { k: KpiSummary }) {
  const meta = KPI_META[k.key];
  if (k.value == null) {
    return (
      <div className="bg-card/50 flex h-full min-h-[168px] flex-col rounded-xl border border-dashed p-5">
        <h3 className="text-muted-foreground text-sm font-medium">{meta.label}</h3>
        <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
          <Info className="h-3 w-3" aria-hidden />
          Not available
        </span>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{k.unavailable}</p>
        <p className="text-muted-foreground mt-auto pt-3 text-xs">{meta.definition}</p>
      </div>
    );
  }
  const line = detailLine(k);
  return (
    <div
      className={cn(
        'bg-card flex h-full min-h-[168px] flex-col rounded-xl border p-5',
        k.key === 'adoption' && 'sm:col-span-2'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-muted-foreground text-sm font-medium" title={meta.definition}>
          {meta.label}
        </h3>
        <DeltaChip k={k} />
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
        {formatKpiValue(k.unit, k.value)}
      </div>
      {line && <div className="text-muted-foreground text-xs">{line}</div>}
      <div className="mt-3">
        <Sparkline series={k.series} />
      </div>
      {k.key === 'adoption' && <AdoptionBars k={k} />}
      <p className="text-muted-foreground mt-auto pt-3 text-xs leading-relaxed">
        {meta.definition} <span className="opacity-80">Source: {meta.source}.</span>
      </p>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="bg-muted/40 h-[168px] animate-pulse rounded-xl border" />
      ))}
    </div>
  );
}

function KpiBody({ data }: { data: PlatformKpis }) {
  const byKey = new Map(data.kpis.map((k) => [k.key, k] as const));
  if (data.daysWithData === 0 && !data.lastComputedAt) {
    return (
      <div className="rounded-xl border border-dashed p-8 text-center">
        <p className="font-medium">No KPI snapshots yet</p>
        <p className="text-muted-foreground mt-1 text-sm">
          The daily rollup runs at 14:00 UTC. Press “Recompute now” to build the last 35 days
          immediately.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {SECTIONS.map((s) => (
        <section key={s.title} className="space-y-3">
          <div>
            <h3 className="text-base font-semibold">{s.title}</h3>
            <p className="text-muted-foreground text-sm">{s.description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {s.keys.map((key) => {
              const k = byKey.get(key);
              return k ? <KpiCard key={key} k={k} /> : null;
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export function PlatformKpiSections({ range }: { range: DateRange }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: qk.platformKpis(range.from, range.to),
    queryFn: () => analyticsEmbedHelpers.getPlatformKpis(range.from, range.to),
    staleTime: 5 * 60_000,
  });
  const recompute = useMutation({
    mutationFn: () => analyticsEmbedHelpers.recomputePlatformKpis(),
    onSuccess: (stats) => {
      if (stats.timedOut) {
        toast.warning('Recompute ran out of time; nothing was saved. Try again.');
      } else if (stats.errors > 0) {
        toast.warning(
          `Recomputed ${stats.daysWritten} days; some sources failed: ${stats.firstError ?? 'see cron runs'}`
        );
      } else {
        toast.success(`Recomputed ${stats.daysWritten} days`);
      }
      queryClient.invalidateQueries({ queryKey: qk.platformKpisAll() });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Recompute failed')),
  });

  const last = query.data?.lastComputedAt
    ? new Date(query.data.lastComputedAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <section className="space-y-5" aria-labelledby="kpi-heading">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="kpi-heading" className="text-lg font-semibold">
            Product KPIs
          </h2>
          <p className="text-muted-foreground text-sm">
            Computed daily from Tyashin&apos;s own data (UTC days), compared with the previous
            period of the same length.
            {last ? ` Last computed ${last}.` : ''}
            {query.data?.partialDays.length ? ' Today is partial until tomorrow’s run.' : ''}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => recompute.mutate()}
          disabled={recompute.isPending}
          className="w-fit shrink-0"
        >
          <RefreshCw className={cn('mr-2 h-4 w-4', recompute.isPending && 'animate-spin')} />
          {recompute.isPending ? 'Recomputing…' : 'Recompute now'}
        </Button>
      </div>
      <DataState query={query} skeleton={<KpiSkeleton />}>
        {(data) => <KpiBody data={data} />}
      </DataState>
    </section>
  );
}
